import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase";

export const INVOICE_STATUS = {
  DRAFT: "draft",
  SENT: "sent",
  PAID: "paid",
  OVERDUE: "overdue",
  VOID: "void",
};

export function calculateTotals(lineItems = [], taxRate = 0, discount = 0) {
  const subtotal = lineItems.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0),
    0
  );
  const taxAmount = subtotal * ((Number(taxRate) || 0) / 100);
  const total = subtotal + taxAmount - (Number(discount) || 0);
  return {
    subtotal: Math.round(subtotal * 100) / 100,
    taxAmount: Math.round(taxAmount * 100) / 100,
    total: Math.round(Math.max(total, 0) * 100) / 100,
  };
}

function round(n) {
  return Math.round(n * 100) / 100;
}

function defaultDueDate(daysFromNow = 14) {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toISOString().split("T")[0];
}

function generateShareId() {
  return (
    Math.random().toString(36).slice(2) +
    Math.random().toString(36).slice(2) +
    Date.now().toString(36)
  );
}

async function getNextInvoiceNumber(userId) {
  const counterRef = doc(db, "users", userId, "company", "counters");
  const snap = await getDoc(counterRef);
  const current = snap.exists() ? snap.data().invoiceCounter || 0 : 0;
  const next = current + 1;
  await setDoc(counterRef, { invoiceCounter: next }, { merge: true });
  return `INV-${String(next).padStart(4, "0")}`;
}

export async function createInvoice(userId, invoiceData) {
  const invoicesRef = collection(db, "users", userId, "invoices");
  const invoiceNumber = await getNextInvoiceNumber(userId);
  const totals = calculateTotals(invoiceData.lineItems, invoiceData.taxRate, invoiceData.discount);

  const docRef = await addDoc(invoicesRef, {
    ...invoiceData,
    ...totals,
    invoiceNumber,
    status: invoiceData.status || INVOICE_STATUS.DRAFT,
    issueDate: invoiceData.issueDate || new Date().toISOString().split("T")[0],
    dueDate: invoiceData.dueDate || defaultDueDate(),
    shareId: generateShareId(),
    amountPaid: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function updateInvoice(userId, invoiceId, updates) {
  const ref = doc(db, "users", userId, "invoices", invoiceId);
  const payload = { ...updates, updatedAt: serverTimestamp() };
  if (updates.lineItems) {
    Object.assign(payload, calculateTotals(updates.lineItems, updates.taxRate, updates.discount));
  }
  await updateDoc(ref, payload);
}

export async function deleteInvoice(userId, invoiceId) {
  await deleteDoc(doc(db, "users", userId, "invoices", invoiceId));
}

export async function getInvoices(userId) {
  const q = query(collection(db, "users", userId, "invoices"), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getInvoice(userId, invoiceId) {
  const snap = await getDoc(doc(db, "users", userId, "invoices", invoiceId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}